import React, { useState, useEffect, useCallback } from 'react';
import chemicalService from '../../../services/chemicalService';
import ProductionFormModal from '../components/ProductionFormModal';
import ProductionDetailsModal from '../components/ProductionDetailsModal';
import { generateProductionPdf } from '../components/productionPdf';

const PAGE_SIZE = 10;

const ProductionRunsTab = () => {
  const [productions, setProductions] = useState([]);
  const [summary, setSummary] = useState({
    this_month_count: 0,
    this_month_output: 0,
    this_month_cost: 0,
  });
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);

  const [showFormModal, setShowFormModal] = useState(false);
  const [detailsId, setDetailsId] = useState(null);
  const [printingId, setPrintingId] = useState(null); // id currently being fetched for PDF

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, fromDate, toDate]);

  const fetchProductions = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: PAGE_SIZE };
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;

      const [listRes, summaryRes] = await Promise.all([
        chemicalService.getProductions(params),
        chemicalService.getProductionsSummary().catch((e) => {
          console.warn('Productions summary endpoint not available:', e.message);
          return { data: { this_month_count: 0, this_month_output: 0, this_month_cost: 0 } };
        }),
      ]);

      setProductions(listRes.data || []);
      setPagination(listRes.pagination || { page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });
      setSummary(summaryRes.data || { this_month_count: 0, this_month_output: 0, this_month_cost: 0 });
    } catch (err) {
      console.error('Error fetching productions:', err);
      alert('❌ ' + (err.response?.data?.message || 'Failed to load productions'));
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, fromDate, toDate]);

  useEffect(() => {
    fetchProductions();
  }, [fetchProductions]);

  const handleDelete = async (p) => {
    if (!window.confirm(
      `Delete production "${p.batch_no}" (${p.product_name})?\n\n` +
      `This will restore the consumed raw materials and delete the output batch. Cannot be undone.`
    )) return;

    try {
      await chemicalService.deleteProduction(p.id);
      alert('✅ Production deleted. Raw material stock restored.');
      if (productions.length === 1 && page > 1) {
        setPage(page - 1);
      } else {
        fetchProductions();
      }
    } catch (err) {
      alert('❌ ' + (err.response?.data?.message || 'Failed to delete production'));
    }
  };

  const handlePrint = async (p) => {
    try {
      setPrintingId(p.id);
      // List rows don't include consumed[] — fetch full details first
      const res = await chemicalService.getProductionById(p.id);
      generateProductionPdf(res.data);
    } catch (err) {
      alert('❌ ' + (err.response?.data?.message || err.message || 'Failed to generate PDF'));
    } finally {
      setPrintingId(null);
    }
  };

  const handleSaved = () => {
    setShowFormModal(false);
    fetchProductions();
  };

  const clearDateFilters = () => {
    setFromDate('');
    setToDate('');
  };

  return (
    <div style={{ padding: '24px 30px 40px' }}>

      {/* ─── Summary Cards ───────────────────────── */}
      <div style={styles.summaryGrid}>
        <div style={{ ...styles.summaryCard, borderLeft: '4px solid #2c3e50' }}>
          <div style={styles.summaryLabel}>Productions This Month</div>
          <div style={{ ...styles.summaryValue, color: '#2c3e50' }}>{summary.this_month_count}</div>
          <div style={styles.summarySub}>batches completed</div>
        </div>
        <div style={{ ...styles.summaryCard, borderLeft: '4px solid #27ae60' }}>
          <div style={styles.summaryLabel}>Total Output</div>
          <div style={{ ...styles.summaryValue, color: '#27ae60' }}>
            {formatQty(summary.this_month_output)}
          </div>
          <div style={styles.summarySub}>this month</div>
        </div>
        <div style={{ ...styles.summaryCard, borderLeft: '4px solid #e74c3c' }}>
          <div style={styles.summaryLabel}>Production Cost</div>
          <div style={{ ...styles.summaryValue, color: '#e74c3c' }}>
            {formatLakh(summary.this_month_cost)}
          </div>
          <div style={styles.summarySub}>this month</div>
        </div>
      </div>

      {/* ─── Filters + Search + Add ──────────────── */}
      <div style={styles.toolbar}>
        <div style={styles.filterTabs}>
          <div style={styles.dateRangeWrap}>
            <label style={styles.dateLabel}>From</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              style={styles.dateInput}
            />
            <label style={styles.dateLabel}>To</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              style={styles.dateInput}
            />
            {(fromDate || toDate) && (
              <button onClick={clearDateFilters} style={styles.clearButton}>
                ✕ Clear
              </button>
            )}
          </div>
        </div>

        <div style={styles.toolbarRight}>
          <div style={styles.searchWrap}>
            <span style={styles.searchIcon}>⌕</span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by batch no, product, formula..."
              style={styles.searchInput}
            />
          </div>
          <button onClick={() => setShowFormModal(true)} style={styles.addButton}>
            ⚗ Run new production
          </button>
        </div>
      </div>

      {/* ─── Productions Table ───────────────────── */}
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.headerRow}>
              <th style={styles.th}>Batch No</th>
              <th style={styles.th}>Product</th>
              <th style={styles.th}>Formula</th>
              <th style={styles.th}>Date</th>
              <th style={{ ...styles.th, textAlign: 'center' }}>Multiplier</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Output</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Cost</th>
              <th style={styles.th}>Status</th>
              <th style={{ ...styles.th, textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" style={styles.noData}>
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>⏳</div>
                  Loading productions...
                </td>
              </tr>
            ) : productions.length === 0 ? (
              <tr>
                <td colSpan="9" style={styles.noData}>
                  <div style={{ fontSize: '40px', marginBottom: '8px' }}>🏭</div>
                  {debouncedSearch || fromDate || toDate
                    ? 'No productions match your filters'
                    : 'No production runs yet — click "⚗ Run new production" to start one'}
                </td>
              </tr>
            ) : (
              productions.map((p) => {
                const expected = parseFloat(p.expected_output) || 0;
                const actual = parseFloat(p.actual_output) || 0;
                const variance = expected > 0 ? ((actual - expected) / expected) * 100 : 0;
                const isPrinting = printingId === p.id;

                return (
                  <tr key={p.id} style={styles.row}>
                    <td style={styles.td}>
                      <div style={{ fontWeight: '700', color: '#2c3e50' }}>{p.batch_no}</div>
                    </td>
                    <td style={styles.td}>
                      <div style={{ fontWeight: '600', color: '#2c3e50' }}>{p.product_name}</div>
                      <div style={{ fontSize: '12px', color: '#95a5a6', marginTop: '2px' }}>
                        {p.product_code}
                      </div>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.formulaTag}>{p.formula_code}</span>
                    </td>
                    <td style={styles.td}>{formatDate(p.production_date)}</td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>
                      <span style={styles.multiplierBadge}>
                        {parseFloat(p.multiplier).toFixed(1)}x
                      </span>
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>
                      <div style={{ fontWeight: '700', color: '#27ae60' }}>
                        {formatQty(p.actual_output)} {p.product_unit}
                      </div>
                      {expected > 0 && (
                        <div style={{
                          fontSize: '11px',
                          color: Math.abs(variance) > 5 ? '#f39c12' : '#95a5a6',
                          marginTop: '2px'
                        }}>
                          {variance >= 0 ? '+' : ''}{variance.toFixed(1)}% vs expected
                        </div>
                      )}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: '600' }}>
                      Rs {formatNumber(p.total_cost)}
                      {actual > 0 && (
                        <div style={{ fontSize: '11px', color: '#95a5a6', marginTop: '2px' }}>
                          Rs {(parseFloat(p.total_cost) / actual).toFixed(2)} / {p.product_unit}
                        </div>
                      )}
                    </td>
                    <td style={styles.td}>
                      <span style={styles.statusBadge}>✓ Completed</span>
                    </td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>
                      <div style={{ display: 'inline-flex', gap: '6px' }}>
                        <button onClick={() => setDetailsId(p.id)} style={styles.viewButton} title="View details">
                          👁️
                        </button>
                        <button
                          onClick={() => handlePrint(p)}
                          disabled={isPrinting}
                          style={{ ...styles.printButton, opacity: isPrinting ? 0.5 : 1, cursor: isPrinting ? 'wait' : 'pointer' }}
                          title="Print PDF"
                        >
                          {isPrinting ? '⏳' : '🖨️'}
                        </button>
                        <button onClick={() => handleDelete(p)} style={styles.deleteButton} title="Delete">
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ─── Pagination ──────────────────────────── */}
      {!loading && pagination.total > 0 && (
        <div style={styles.paginationWrap}>
          <div style={styles.paginationInfo}>
            Showing {(pagination.page - 1) * pagination.limit + 1}
            –{Math.min(pagination.page * pagination.limit, pagination.total)}
            {' '}of {pagination.total} productions
          </div>
          <div style={styles.paginationButtons}>
            <button
              onClick={() => setPage(1)}
              disabled={page === 1}
              style={{ ...styles.pageButton, ...(page === 1 ? styles.pageButtonDisabled : {}) }}
            >
              «
            </button>
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              style={{ ...styles.pageButton, ...(page === 1 ? styles.pageButtonDisabled : {}) }}
            >
              ‹ Prev
            </button>
            <span style={styles.pageIndicator}>
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page >= pagination.totalPages}
              style={{ ...styles.pageButton, ...(page >= pagination.totalPages ? styles.pageButtonDisabled : {}) }}
            >
              Next ›
            </button>
            <button
              onClick={() => setPage(pagination.totalPages)}
              disabled={page >= pagination.totalPages}
              style={{ ...styles.pageButton, ...(page >= pagination.totalPages ? styles.pageButtonDisabled : {}) }}
            >
              »
            </button>
          </div>
        </div>
      )}

      {/* ─── Modals ─────────────────────────────── */}
      {showFormModal && (
        <ProductionFormModal
          onClose={() => setShowFormModal(false)}
          onSaved={handleSaved}
        />
      )}

      {detailsId && (
        <ProductionDetailsModal
          productionId={detailsId}
          onClose={() => setDetailsId(null)}
        />
      )}

    </div>
  );
};

// ─── Helpers ──────────────────────────────────────────────

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatNumber = (n) => {
  const num = parseFloat(n) || 0;
  return Math.round(num).toLocaleString('en-PK');
};

const formatQty = (n) => {
  const num = parseFloat(n) || 0;
  return num % 1 === 0 ? num.toLocaleString('en-PK') : num.toFixed(2);
};

const formatLakh = (n) => {
  const num = parseFloat(n) || 0;
  if (num >= 1000000) return `Rs ${(num / 1000000).toFixed(2)}M`;
  return `Rs ${formatNumber(num)}`;
};

// ─── Styles ───────────────────────────────────────────────

const styles = {
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  summaryCard: {
    background: 'white',
    padding: '18px 22px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  },
  summaryLabel: {
    fontSize: '13px', color: '#7f8c8d',
    marginBottom: '6px', fontWeight: '600',
  },
  summaryValue: { fontSize: '28px', fontWeight: '700' },
  summarySub: { fontSize: '11px', color: '#95a5a6', marginTop: '4px' },

  toolbar: {
    background: 'white',
    padding: '16px 20px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    display: 'flex', flexWrap: 'wrap', gap: '16px',
    justifyContent: 'space-between', alignItems: 'center',
    marginBottom: '16px',
  },
  filterTabs: { display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' },
  dateRangeWrap: {
    display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
  },
  dateLabel: { fontSize: '12px', fontWeight: '600', color: '#7f8c8d' },
  dateInput: {
    padding: '8px 10px',
    border: '2px solid #e0e0e0', borderRadius: '8px',
    fontSize: '13px', fontFamily: 'inherit',
  },
  clearButton: {
    padding: '8px 12px',
    background: '#f0f0f0', color: '#7f8c8d',
    border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: '12px', fontWeight: '600',
  },
  toolbarRight: { display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' },
  searchWrap: { position: 'relative' },
  searchIcon: {
    position: 'absolute', left: '12px', top: '50%',
    transform: 'translateY(-50%)',
    color: '#95a5a6', fontSize: '16px',
  },
  searchInput: {
    padding: '10px 12px 10px 36px',
    border: '2px solid #e0e0e0', borderRadius: '8px',
    fontSize: '14px', width: '280px',
    fontFamily: 'inherit',
  },
  addButton: {
    padding: '10px 20px',
    background: '#e74c3c', color: 'white',
    border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px', fontWeight: '600',
  },

  tableWrap: {
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    overflow: 'auto',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  headerRow: { background: '#f8f9fa' },
  th: {
    padding: '14px 16px', textAlign: 'left',
    fontSize: '13px', fontWeight: '700',
    color: '#2c3e50', borderBottom: '2px solid #e0e0e0',
    whiteSpace: 'nowrap',
  },
  row: { borderBottom: '1px solid #f0f0f0' },
  td: { padding: '12px 16px', fontSize: '14px', color: '#2c3e50' },
  noData: {
    padding: '60px 20px', textAlign: 'center',
    color: '#95a5a6', fontSize: '14px',
  },

  formulaTag: {
    background: '#fff3cd', color: '#856404',
    padding: '4px 10px', borderRadius: '10px',
    fontSize: '12px', fontWeight: '700',
  },
  multiplierBadge: {
    background: '#e3f2fd', color: '#1565c0',
    padding: '4px 10px', borderRadius: '10px',
    fontSize: '12px', fontWeight: '700',
  },
  statusBadge: {
    background: '#d4edda', color: '#155724',
    padding: '4px 10px', borderRadius: '10px',
    fontSize: '12px', fontWeight: '600',
    whiteSpace: 'nowrap',
  },

  viewButton: {
    padding: '6px 10px',
    background: '#3498db', color: 'white',
    border: 'none', borderRadius: '6px',
    cursor: 'pointer', fontSize: '14px',
  },
  printButton: {
    padding: '6px 10px',
    background: '#2c3e50', color: 'white',
    border: 'none', borderRadius: '6px',
    cursor: 'pointer', fontSize: '14px',
  },
  deleteButton: {
    padding: '6px 10px',
    background: '#e74c3c', color: 'white',
    border: 'none', borderRadius: '6px',
    cursor: 'pointer', fontSize: '14px',
  },

  paginationWrap: {
    marginTop: '16px',
    background: 'white',
    padding: '14px 20px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', flexWrap: 'wrap', gap: '12px',
  },
  paginationInfo: { fontSize: '13px', color: '#7f8c8d' },
  paginationButtons: { display: 'flex', gap: '6px', alignItems: 'center' },
  pageButton: {
    padding: '8px 14px',
    background: 'white', color: '#2c3e50',
    border: '2px solid #e0e0e0', borderRadius: '8px',
    cursor: 'pointer', fontSize: '13px', fontWeight: '600',
  },
  pageButtonDisabled: { opacity: 0.4, cursor: 'not-allowed' },
  pageIndicator: {
    padding: '0 12px', fontSize: '13px',
    color: '#2c3e50', fontWeight: '600',
  },
};

export default ProductionRunsTab;